// Core Modules
import { Route, Routes } from '@angular/router';

// Guards
import { AuthGuard } from '@core/guards/auth/auth.guard';
import { PermissionGuard } from '@core/guards/permission/permission.guard';
import { SessionValidationGuard } from '@core/guards/session-validation/session-validation.guard';

// Resolvers
import { FederatedDomainResolver } from '@core/resolvers/federated-domain.resolver';

// Components
import { AuthLoginComponent } from '@shared/components/auth-login/auth-login.component';
import { NoAccessComponent } from '@shared/components/no-access/no-access.component';
import { UnauthorizedComponent } from '@shared/components/unauthorized/unauthorized.component';

// Enums
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';

// Navigation
import { NAV_AREAS } from '@layout/side-nav/nav.model';

// Routes
// Swapped for `dev-only.routes.prod.ts` (an empty array, and no `import()`) by `angular.json`'s
// production `fileReplacements` — see that file for why a runtime flag was not enough.
import { devOnlyRoutes } from '@features/ui-showcase/dev-only.routes';

/**
 * Build a lazily-loaded, permission-gated feature-area route.
 * @param path route base (from MainNavigationEnum)
 * @param loadChildren dynamic import of the area's routes file
 * @param permission app-permission that gates the area
 * @returns the configured Route
 */
const featureRoutes = (path: string, loadChildren: Route['loadChildren'], permission: string): Route => ({
  path,
  loadChildren,
  canActivate: [SessionValidationGuard, PermissionGuard],
  data: { permissions: { only: [permission], redirectTo: MainNavigationEnum.UNAUTHORIZED } },
});

/**
 * Builds the app's route table.
 *
 * Takes the dev-only routes as a parameter rather than reading `environment.production` inline
 * so the production shape is assertable: the test build swaps `@env/environment` for
 * `environment.mock.ts` via `fileReplacements`, which puts the production branch out of reach
 * of a spec that can only observe the module-level `routes` below. The showcase gate is the one
 * route whose presence depends on the build, and an untested gate is one refactor away from
 * being dropped.
 *
 * It takes the **routes**, not an `isProduction` flag, so the `import()` behind them sits inside
 * the ternary at the bottom of this file rather than inside this function. A flag is a runtime
 * argument, so the bundler cannot prove the branch dead and emitted the showcase as a real
 * ~25 KB lazy chunk on the production CDN — unroutable, but served. `app.routes.spec.ts` asserts
 * both shapes by calling this directly.
 *
 * @param devOnlyRoutes routes registered only outside production; empty in a production build
 * @returns the configured route table
 */
export const buildRoutes = (devOnlyRoutes: Routes): Routes => [
  {
    path: MainNavigationEnum.LOGIN,
    component: AuthLoginComponent,
    canActivate: [AuthGuard],
    resolve: { data: FederatedDomainResolver },
    title: 'Login',
  },

  // Feature areas, derived from the nav registry so the rail, the router and the landing page
  // cannot disagree. Add a screen in NAV_AREAS, not here.
  ...NAV_AREAS.map((area) => featureRoutes(area.path, area.loadChildren, area.permission)),

  // Dev-only routes — today just the shared-component showcase. See the bottom of this file.
  ...devOnlyRoutes,

  {
    path: MainNavigationEnum.UNAUTHORIZED,
    component: UnauthorizedComponent,
    pathMatch: 'full',
    canActivate: [SessionValidationGuard],
    title: 'Unauthorized',
  },
  {
    path: MainNavigationEnum.NO_ACCESS,
    component: NoAccessComponent,
    pathMatch: 'full',
    canActivate: [SessionValidationGuard],
    title: 'No access',
  },
  // Redirect to /login if match is ""
  { path: '', redirectTo: `/${MainNavigationEnum.LOGIN}`, pathMatch: 'full' },
  // Redirect to /login if no route match is found
  { path: '**', redirectTo: `/${MainNavigationEnum.LOGIN}` },
];

export const routes: Routes = buildRoutes(devOnlyRoutes);
